// RuntimeConsolePanel — right-side drawer that surfaces runtime errors and
// warnings forwarded from the preview iframe. Closed by default; opens via a
// "Console" button on the preview toolbar. Each entry has an "Ask AI to fix"
// button so non-technical users can hand the stack trace back to the agent
// without leaving the editor.
//
// Pop-out: opens a separate browser window rendering the same log so the user
// can still see errors when the preview itself is blank. The popped-out
// window subscribes to a BroadcastChannel for live updates.

import React from "react";
import {
  Badge,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import LucideIcon from "../../components/LucideIcon";
import type { RuntimeEvent, RuntimeEventKind } from "./runtime/AppRuntime";

export interface ConsoleEntry extends RuntimeEvent {
  id: string;
  fingerprint: string;
  count: number;
  /** Set when this event triggered an automatic chat message. */
  autoSent?: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  entries: ConsoleEntry[];
  onClear: () => void;
  onSendToChat: (entry: ConsoleEntry) => void;
  /** Used to namespace the BroadcastChannel for the popped-out window. */
  appId: string;
}

const KIND_LABEL: Record<RuntimeEventKind, string> = {
  uncaught: "Error",
  rejection: "Promise",
  error: "Error",
  warn: "Warning",
  compile: "Compile",
};

const KIND_COLOR: Record<RuntimeEventKind, "error" | "warning" | "default"> = {
  uncaught: "error",
  rejection: "error",
  error: "error",
  warn: "warning",
  compile: "error",
};

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour12: false });
}

const RuntimeConsolePanel: React.FC<Props> = ({
  open,
  onClose,
  entries,
  onClear,
  onSendToChat,
  appId,
}) => {
  // Mirror entries to a BroadcastChannel so a popped-out window can render
  // the same log. We post on every change; the popped-out window also asks
  // for a snapshot via a 'sync-request' message when it opens.
  const channelRef = React.useRef<BroadcastChannel | null>(null);
  React.useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const ch = new BroadcastChannel(`builder-console-${appId}`);
    channelRef.current = ch;
    const onMsg = (ev: MessageEvent) => {
      if (ev.data?.type === "sync-request") {
        ch.postMessage({ type: "snapshot", entries });
      }
    };
    ch.addEventListener("message", onMsg);
    return () => {
      ch.removeEventListener("message", onMsg);
      ch.close();
      channelRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);
  React.useEffect(() => {
    channelRef.current?.postMessage({ type: "snapshot", entries });
  }, [entries]);

  const popOut = () => {
    const w = window.open(
      "",
      `builder-console-${appId}`,
      "width=720,height=480,resizable=yes,scrollbars=yes",
    );
    if (!w) return;
    w.document.write(buildPopOutHtml(appId));
    w.document.close();
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        width: { xs: "100%", sm: 420 },
        maxWidth: "100%",
        bgcolor: "background.paper",
        borderLeft: 1,
        borderColor: "divider",
        boxShadow: 6,
        zIndex: 5,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          Console{entries.length > 0 ? ` (${entries.length})` : ""}
        </Typography>
        <Tooltip title="Open in a separate window">
          <IconButton size="small" onClick={popOut}>
            <LucideIcon name="ExternalLink" size={18} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Clear">
          <span>
            <IconButton size="small" onClick={onClear} disabled={entries.length === 0}>
              <LucideIcon name="Trash2" size={18} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose}>
            <LucideIcon name="X" size={18} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", p: 1 }}>
        {entries.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No errors yet. Runtime errors and warnings from your app will
              appear here, and the AI will try to fix new ones automatically.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {entries.map((e) => (
              <Box
                key={e.id}
                sx={{
                  p: 1.5,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "action.hover",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 0.5 }}
                >
                  <Chip
                    size="small"
                    label={KIND_LABEL[e.kind]}
                    color={KIND_COLOR[e.kind]}
                    variant={KIND_COLOR[e.kind] === "default" ? "outlined" : "filled"}
                  />
                  {e.count > 1 && (
                    <Badge
                      badgeContent={e.count}
                      color="default"
                      sx={{ "& .MuiBadge-badge": { position: "static", transform: "none" } }}
                    />
                  )}
                  {e.autoSent && (
                    <Chip
                      size="small"
                      icon={<LucideIcon name="Sparkles" size={14} />}
                      label="Asked AI"
                      variant="outlined"
                    />
                  )}
                  <Box sx={{ flex: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    {formatTime(e.ts)}
                  </Typography>
                </Stack>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: 12,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {e.message}
                </Typography>
                {e.stack && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="pre"
                    sx={{
                      mt: 0.5,
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      fontSize: 11,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      maxHeight: 160,
                      overflow: "auto",
                      m: 0,
                    }}
                  >
                    {e.stack}
                  </Typography>
                )}
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<LucideIcon name="Sparkles" size={16} />}
                    onClick={() => onSendToChat(e)}
                    disabled={e.autoSent}
                  >
                    {e.autoSent ? "Sent to AI" : "Ask AI to fix"}
                  </Button>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

// Self-contained HTML for the popped-out console window. It listens on the
// same BroadcastChannel for snapshots and renders a minimal log. No build
// step — vanilla HTML/CSS/JS so we can drop it into a fresh window.
function buildPopOutHtml(appId: string): string {
  const channel = `builder-console-${appId}`;
  return `<!DOCTYPE html>
<html><head>
<meta charset="utf-8" />
<title>Preview Console</title>
<style>
  body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; background: #0b0b0b; color: #eee; }
  header { padding: 8px 12px; border-bottom: 1px solid #2a2a2a; font-weight: 600; }
  #log { padding: 8px; }
  .entry { padding: 8px; margin-bottom: 6px; border: 1px solid #2a2a2a; border-radius: 4px; background: #151515; }
  .meta { font-size: 11px; color: #888; margin-bottom: 4px; }
  .kind { display: inline-block; padding: 1px 6px; border-radius: 3px; font-size: 10px; margin-right: 6px; text-transform: uppercase; }
  .kind.error, .kind.uncaught, .kind.rejection, .kind.compile { background: #5a1d1d; color: #ffb4b4; }
  .kind.warn { background: #5a4a1d; color: #ffe4a4; }
  .msg { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; white-space: pre-wrap; word-break: break-word; }
  .stack { margin-top: 4px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11px; color: #aaa; white-space: pre-wrap; word-break: break-word; max-height: 200px; overflow: auto; }
  .empty { padding: 16px; color: #888; font-size: 13px; }
</style>
</head><body>
<header>Preview Console</header>
<div id="log"><div class="empty">Waiting for events…</div></div>
<script>
(function () {
  var KIND = { uncaught: "Error", rejection: "Promise", error: "Error", warn: "Warning", compile: "Compile" };
  var ch = new BroadcastChannel(${JSON.stringify(channel)});
  function escape(s) { return String(s == null ? "" : s).replace(/[&<>]/g, function (c) { return ({"&":"&amp;","<":"&lt;",">":"&gt;"})[c]; }); }
  function fmt(entries) {
    var log = document.getElementById("log");
    if (!entries || entries.length === 0) {
      log.innerHTML = '<div class="empty">No errors yet.</div>';
      return;
    }
    log.innerHTML = entries.map(function (e) {
      var time = new Date(e.ts).toLocaleTimeString([], { hour12: false });
      var stack = e.stack ? '<div class="stack">' + escape(e.stack) + '</div>' : '';
      return '<div class="entry">' +
        '<div class="meta"><span class="kind ' + e.kind + '">' + (KIND[e.kind] || e.kind) + '</span>' +
        (e.count > 1 ? '×' + e.count + ' · ' : '') + time + '</div>' +
        '<div class="msg">' + escape(e.message) + '</div>' + stack +
        '</div>';
    }).join("");
  }
  ch.onmessage = function (ev) {
    if (ev.data && ev.data.type === "snapshot") fmt(ev.data.entries);
  };
  ch.postMessage({ type: "sync-request" });
  window.addEventListener("beforeunload", function () { ch.close(); });
})();
</script>
</body></html>`;
}

export default RuntimeConsolePanel;
